import { useState, useEffect, useCallback, useRef } from "react";
import { FaPrint, FaTrash, FaMoneyBillWave, FaUserMd } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import { HiXMark } from "react-icons/hi2";
import { PathologyPrintSlip } from "./PathologyPrintSlip";
import ConfirmActionModal from "../ConfirmDeleteModal";
import DynamicSelect from "../DynamicSelect";
import { useAuth } from "../../utils/AuthContext";

// --- CONFIGURATION ---
const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const BASE_URL = "https://api.muktihospital.com/api";

const PathologyAddPaymentModal = ({
  isOpen,
  onClose,
  bill: initialBill,
  onPaymentSuccess,
}) => {

  const { user } = useAuth();
  const userRole = (user?.role || "").toLowerCase();
  const canGiveHospitalDiscount = ["pathologist", "admin", "superadmin", "super admin"].includes(userRole);
  const [billDetails, setBillDetails] = useState(initialBill);
  const [patientPhone, setPatientPhone] = useState("");
  const [transactions, setTransactions] = useState(initialBill?.payments || []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [doctors, setDoctors] = useState([]);

  const [confirmDelete, setConfirmDelete] = useState({
    show: false,
    paymentId: null,
  });
  const [printData, setPrintData] = useState(null);

  // Forms
  const [patientForm, setPatientForm] = useState({
    date: new Date().toISOString().split("T")[0],
    amount: "",
    discount: "",
    paymentMode: "Cash",
    note: "",
    discountSource: "agent",
    discountType: "taka",
  });

  const [doctorForm, setDoctorForm] = useState({
    doctorId: null,
    amount: "",
    note: "",
    date: new Date().toISOString().split("T")[0],
  });

  // 1. Fetch Doctors List
  useEffect(() => {
    if (isOpen) {
      axios
        .get(`${BASE_URL}/doctor`, { headers: { "x-api-key": API_KEY } })
        .then((res) => {
          const rawData = Array.isArray(res.data) ? res.data : res.data.data || [];
          const options = rawData.map((d) => {
            let name = d.name || "Unknown";
            if (d.translations) {
              try {
                const t = typeof d.translations === "string" ? JSON.parse(d.translations) : d.translations;
                name = t?.en?.name || t?.bn?.name || name;
              } catch (e) {}
            }
            return {
              value: d.id,
              label: name + (d.specialization ? ` (${d.specialization})` : ""),
            };
          });
          setDoctors(options);
        })
        .catch((err) => console.error("Error fetching doctors:", err));
    }
  }, [isOpen]);

  // 2. Fetch Latest Bill Data
  const fetchLatestData = useCallback(async () => {
    if (!initialBill?.id) return;
    try {
      const res = await axios.get(
        `${BASE_URL}/pathology-bill/${initialBill.id}`,
        { headers: { "x-api-key": API_KEY } }
      );
      const fullData = res.data.bill;

      setBillDetails(fullData);
      setTransactions(fullData.payments || []);
      setPatientPhone(fullData.patient?.phoneNumber || fullData.patientPhone || "");

      const net = parseFloat(fullData.netAmount) || 0;
      const paid = parseFloat(fullData.paymentAmount || fullData.paidAmount || 0);
      const due = net - paid;

      setPatientForm((prev) => ({
        ...prev,
        amount: due > 0 ? due : "",
      }));
    } catch (error) {
      console.error("Error fetching bill data:", error);
    }
  }, [initialBill?.id]);

  useEffect(() => {
    if (isOpen && initialBill) {
      setBillDetails(initialBill);
      setTransactions(initialBill.payments || []);

      const net = parseFloat(initialBill.netAmount) || 0;
      const paid = parseFloat(initialBill.paymentAmount || initialBill.paidAmount || 0);
      const due = net - paid;

      setPatientForm((prev) => ({
        ...prev,
        amount: due > 0 ? due : "",
      }));

      fetchLatestData();
    }
  }, [isOpen, initialBill, fetchLatestData]);

  const discountInitedRef = useRef(false);
  useEffect(() => {
    if (isOpen && initialBill && !discountInitedRef.current) {
      discountInitedRef.current = true;
      setPatientForm((prev) => ({
        ...prev,
        discountSource: initialBill.discountSource || "agent",
      }));
    }
    if (!isOpen) discountInitedRef.current = false;
  }, [isOpen, initialBill]);


  useEffect(() => {
    const pid = initialBill?.patientId;
    if (!isOpen || !pid) return;
    (async () => {
      try {
        const res = await axios.get("https://api.muktihospital.com/api/patient", {
          headers: { "x-api-key": API_KEY },
        });
        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data.data)
          ? res.data.data
          : [];
        const found = list.find((p) => String(p.id) === String(pid));
        if (found && found.phoneNumber) setPatientPhone(found.phoneNumber);
      } catch (e) {
        console.error("Error fetching patient phone:", e);
      }
    })();
  }, [isOpen, initialBill?.patientId]);


  const computeAutoDiscount = (amountVal, source) => {
    const net = parseFloat(billDetails?.netAmount) || 0;
    const paid = parseFloat(billDetails?.paymentAmount || billDetails?.paidAmount || 0);
    const due = net - paid;
    const commission = parseFloat(billDetails?.netReferAmount ?? billDetails?.referAmount) || 0;
    const amt = parseFloat(amountVal) || 0;
    const shortfall = Math.max(0, due - amt);
    let disc = shortfall;
    if (source === "agent") disc = Math.min(shortfall, commission);
    return disc > 0 ? String(Math.round(disc)) : "";
  };

  const effectiveDiscountAmount = () => {
    const val = parseFloat(patientForm.discount) || 0;
    if (patientForm.discountType === "percentage") {
      const net = parseFloat(billDetails?.netAmount) || 0;
      const paid = parseFloat(billDetails?.paymentAmount || billDetails?.paidAmount || 0);
      const due = Math.max(0, net - paid);
      return Math.round((due * val) / 100);
    }
    return Math.round(val);
  };

  useEffect(() => {
    const net = parseFloat(billDetails?.netAmount) || 0;
    const paid = parseFloat(billDetails?.paymentAmount || billDetails?.paidAmount || 0);
    const due = Math.max(0, net - paid);
    const disc = effectiveDiscountAmount();
    const newAmount = Math.max(0, due - disc);
    setPatientForm((prev) => {
      if (String(prev.amount) === String(newAmount)) return prev;
      return { ...prev, amount: newAmount };
    });
  }, [patientForm.discount, patientForm.discountType, patientForm.discountSource, billDetails]);

  const agentCommission = parseFloat(billDetails?.netReferAmount ?? billDetails?.referAmount) || 0;

  const modeOptions = [
    { value: "Cash", label: "Cash" },
    { value: "Card", label: "Card" },
    { value: "Mobile Banking", label: "Mobile Banking" },
  ];
  const discountSourceOptions = [
    { value: "agent", label: "Agent" },
    { value: "hospital", label: "Hospital" },
    { value: "doctor", label: "Doctor" },
  ];

  const handlePatientSubmit = async (e) => {
    e.preventDefault();

    // Guard: agent discount (in ৳) can never exceed the agent's commission
    const discNow = effectiveDiscountAmount();
    if (patientForm.discountSource === "agent" && discNow > agentCommission) {
      return toast.error(`Agent discount can't exceed commission (৳${agentCommission}).`);
    }

    const remainingDue =
      (billDetails?.netAmount || 0) -
      (billDetails?.paymentAmount || billDetails?.paidAmount || 0);
    if (
      parseFloat(patientForm.amount || 0) > remainingDue &&
      remainingDue > 0
    ) {
      toast.warning("Warning: Payment exceeds due amount.");
    }

    const discountNow = effectiveDiscountAmount();
    if (patientForm.discountSource === "hospital" && discountNow > 0) {
      if (!canGiveHospitalDiscount) {
        toast.error("Only Pathologist / Admin can give a hospital discount here.");
        return;
      }
      if (!patientForm.note || patientForm.note.trim().length < 4) {
        toast.error("A reason is required for a hospital discount.");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('authToken');

      const payload = {
        billId: billDetails.id,
        paymentDate: patientForm.date,
        amount: parseFloat(patientForm.amount || 0),
        discount: effectiveDiscountAmount(),
        discountSource: patientForm.discountSource,
        freeBillReason:
          patientForm.discountSource === "hospital" && effectiveDiscountAmount() > 0
            ? patientForm.note
            : null,
        freeBillReason:
          patientForm.discountSource === "hospital" && effectiveDiscountAmount() > 0
            ? patientForm.note
            : null,
        paymentMode: patientForm.paymentMode,
        note: patientForm.note,
        userName: user?.name || "Unknown Staff", 
        doctorId: null,
        doctorAmount: null,
        doctorNote: null,
      };

      await axios.post(`${BASE_URL}/pathology-bill/add-payment`, payload, {
        headers: { 
          "x-api-key": API_KEY,
          "Authorization": token ? `Bearer ${token}` : "" 
        },
      });

      toast.success("Payment Received!");
      if (onPaymentSuccess) onPaymentSuccess();
      await fetchLatestData();
      setPatientForm((prev) => ({
        ...prev,
        amount: "",
        discount: "",
        note: "",
      }));
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to add payment");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDoctorSubmit = async (e) => {
    e.preventDefault();
    if (!doctorForm.doctorId || !doctorForm.amount) {
      return toast.warning("Please select a doctor and amount.");
    }

    // Hospital discount from Add Money requires a reason and a permitted role
    const discountNow = effectiveDiscountAmount();
    if (patientForm.discountSource === "hospital" && discountNow > 0) {
      if (!canGiveHospitalDiscount) {
        toast.error("Only Pathologist / Admin can give a hospital discount here.");
        return;
      }
      if (!patientForm.note || patientForm.note.trim().length < 4) {
        toast.error("A reason is required for a hospital discount.");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('authToken');

      const payload = {
        billId: billDetails.id,
        paymentDate: doctorForm.date,
        amount: 0,
        discount: 0,
        paymentMode: "Cash",
        note: "Doctor Payment",
        doctorId: doctorForm.doctorId,
        doctorAmount: parseFloat(doctorForm.amount),
        doctorNote: doctorForm.note,
        userName: user?.name || "Unknown Staff", 
      };

      await axios.post(`${BASE_URL}/pathology-bill/add-payment`, payload, {
        headers: { 
          "x-api-key": API_KEY,
          "Authorization": token ? `Bearer ${token}` : "" 
        },
      });

      toast.success("Doctor Paid!");
      await fetchLatestData();
      setDoctorForm((prev) => ({ ...prev, amount: "", note: "" }));
    } catch (error) {
      toast.error("Failed to pay doctor");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Print Logic
  useEffect(() => {
    if (printData) {
      const timer = setTimeout(() => {
        const printLayout = document.querySelector(".payment-history-print");
        if (printLayout) {
          printLayout.style.display = "block";
          window.print();
          setTimeout(() => {
            printLayout.style.display = "none";
            printLayout.innerHTML = "";
            setPrintData(null);
          }, 500);
        } else {
          setPrintData(null);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [printData]);

  // Delete Transaction
  const initiateDelete = (paymentId) => setConfirmDelete({ show: true, paymentId });

  const executeDelete = async () => {
    if (!confirmDelete.paymentId) return;
    try {
      await axios.delete(
        `${BASE_URL}/pathology-bill/delete-payment/${confirmDelete.paymentId}`,
        { headers: { "x-api-key": API_KEY } }
      );
      toast.success("Transaction Deleted!");
      if (onPaymentSuccess) onPaymentSuccess();
      await fetchLatestData();
      setConfirmDelete({ show: false, paymentId: null });
    } catch (error) {
      toast.error("Failed to delete transaction.");
    }
  };

  // Convert number to Bangladeshi words (Lakh/Crore)
  const numberToWordsBD = (value) => {
    let num = Math.floor(Number(value) || 0);
    if (num === 0) return "Zero";
    const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    const two = (n) => (n < 20 ? ones[n] : tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : ""));
    const three = (n) => {
      const h = Math.floor(n / 100);
      const r = n % 100;
      return (h ? ones[h] + " Hundred" + (r ? " " : "") : "") + (r ? two(r) : "");
    };
    let words = "";
    const crore = Math.floor(num / 10000000); num %= 10000000;
    const lakh = Math.floor(num / 100000); num %= 100000;
    const thousand = Math.floor(num / 1000); num %= 1000;
    if (crore) words += three(crore) + " Crore ";
    if (lakh) words += two(lakh) + " Lakh ";
    if (thousand) words += two(thousand) + " Thousand ";
    if (num) words += three(num);
    return words.trim();
  };

  const parseTestsData = (t) => {
    if (Array.isArray(t)) return t;
    if (typeof t === "string") {
      try { const p = JSON.parse(t); return Array.isArray(p) ? p : []; } catch { return []; }
    }
    return [];
  };

  // Prints the FULL bill slip (same design as Add Bill), not a payment receipt.
  const handlePrintReceipt = (tx) => {
    const bd = billDetails || {};
    const tests = parseTestsData(bd.tests);
    const total = Number(bd.total) || 0;
    const discount = Number(bd.discount) || 0;
    const netAmount = Number(bd.netAmount) || 0;
    const paidAmount = Number(bd.paymentAmount) || 0;
    const dueAmount = bd.dueAmount != null ? Number(bd.dueAmount) : Math.max(0, netAmount - paidAmount);
    const isPaid = dueAmount <= 0;

    setPrintData({
      title: "Pathology Invoice",
      isPaid,
      isFree: netAmount === 0,
      note: (bd.freeBillReason || (bd.note && bd.note.includes("Free"))) ? (
        <div className="note-box"><strong>Note:</strong> {bd.freeBillReason || bd.note}</div>
      ) : null,
      amountInWords: numberToWordsBD(netAmount) + " Taka Only" + (netAmount === 0 ? " (100% Hospital Free)" : ""),
      billInfo: (
        <>
          <div>
            <p><strong>Bill No:</strong> {bd.billNumber || "N/A"}</p>
            <p><strong>Patient Name:</strong> {bd.patientName || "N/A"}</p>
            <p><strong>Mobile:</strong> {patientPhone || bd.patient?.phoneNumber || bd.patientPhone || "N/A"}</p>
          </div>
          <div>
            <p>
              <strong>Date:</strong>{" "}
              {bd.createdAt
                ? `${new Date(bd.createdAt).toLocaleDateString("en-GB")} ${new Date(bd.createdAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "numeric", hour12: true })}`
                : "N/A"}
            </p>
            <p><strong>Age:</strong> {bd.age || "N/A"}</p>
            <p><strong>Donor:</strong> {bd.donorName || "N/A"}</p>
          </div>
          <p><strong>Doctor Name:</strong> {bd.doctorName || "N/A"}</p>
          {(bd.refer || bd.referDoctorName) && (
            <p><strong>Referred By:</strong> {bd.refer || bd.referDoctorName}</p>
          )}
        </>
      ),
      table: (
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 sm:p-3 border font-semibold">Test Name</th>
              <th className="p-2 sm:p-3 border font-semibold text-right">Amount (৳)</th>
            </tr>
          </thead>
          <tbody>
            {tests.map((t, i) => (
              <tr key={i} className="border-t">
                <td className="p-2 sm:p-3 border">{t.testName || "N/A"}</td>
                <td className="p-2 sm:p-3 border text-right">{(Number(t.amount) || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ),
      summary: (
        <div className="flex flex-col">
          <div className="flex justify-between"><span>Total:</span><span>৳{total.toFixed(2)}</span></div>
          <div className="flex justify-between"><span>Discount ({bd.discountType === "percentage" ? "%" : "৳"}):</span><span>{discount.toFixed(2)}</span></div>
          <div className="flex justify-between"><span>Net Amount:</span><span>৳{netAmount.toFixed(2)}</span></div>
          <div className="flex justify-between"><span>Net Payable:</span><span>৳{paidAmount.toFixed(2)}</span></div>
          {dueAmount > 0 && (
            <div className="flex justify-between"><span>Due:</span><span>৳{dueAmount.toFixed(2)}</span></div>
          )}
        </div>
      ),
      footer: (
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "30px", paddingLeft: "10px", paddingRight: "10px" }}>
          <div style={{ textAlign: "center", width: "220px" }}>
            <div style={{ borderTop: "1px solid #000", marginBottom: "8px" }}></div>
            <p style={{ margin: 0, fontWeight: "bold", fontSize: "11pt", textTransform: "capitalize" }}>{bd.userName || "N/A"}</p>
            <p style={{ margin: "5px 0 0", fontSize: "9pt" }}>Prepared By</p>
          </div>
          <div style={{ textAlign: "center", width: "220px" }}>
            <div style={{ borderTop: "1px solid #000", marginBottom: "8px" }}></div>
            <p style={{ margin: 0, fontWeight: "bold", fontSize: "11pt" }}>{bd.authorizedBy || "N/A"}</p>
            <p style={{ margin: "5px 0 0", fontSize: "9pt" }}>Authorized By</p>
          </div>
        </div>
      ),
    });
  };

  if (!isOpen) return null;

  const activeBill = billDetails || initialBill || {};
  const currentTotal = activeBill.total || activeBill.netAmount || 0;
  const currentPaid = activeBill.paymentAmount || activeBill.paidAmount || 0;
  const currentDue = (activeBill.netAmount || currentTotal) - currentPaid;

  const discountMoney = (activeBill.discountAmount && activeBill.discountAmount > 0)
    ? activeBill.discountAmount
    : (activeBill.discountType === "percentage"
        ? Math.round((((activeBill.discountSource === "agent" ? (activeBill.referAmount || 0) : (activeBill.total || 0))) * (activeBill.discount || 0)) / 100)
        : (activeBill.discount || 0));

  const InfoRow = ({ label, value, isBold, className = "" }) => (
    <div className={`flex justify-between border-b border-gray-100 py-2 text-sm last:border-0 ${className}`}>
      <span className="text-gray-500 font-medium">{label}</span>
      <span className={`text-right text-gray-800 ${isBold ? "font-bold" : ""}`}>{value || "-"}</span>
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 z-40 flex items-start sm:items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0 overflow-y-auto print-hidden">
        <div className="bg-white shadow-lg w-full my-4 sm:my-0 max-h-[100vh] h-full overflow-y-auto">
          {/* Header */}
          <div className="flex justify-between items-center p-4 bg-gray-200 rounded-t-lg border-b border-gray-300">
            <h2 className="text-lg font-bold">Manage Pathology Payments</h2>
            <button onClick={onClose} className="size-7 rounded-full bg-white text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300">
              <HiXMark />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* LEFT: Bill Details */}
              <div className="lg:col-span-4 space-y-4">
                <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
                  <h3 className="text-gray-700 font-bold border-b pb-2 mb-3 text-sm uppercase">Bill Summary</h3>
                  <InfoRow label="Bill No" value={activeBill.billNumber} isBold />
                  <InfoRow label="Total Bill" value={`৳ ${currentTotal}`} />
                  <InfoRow label="Total Discount" value={activeBill.discountType === "percentage" ? `${activeBill.discount || 0}%  (৳ ${Number(discountMoney).toLocaleString()})` : `৳ ${Number(discountMoney).toLocaleString()}`} className="text-blue-600" />
                  <InfoRow label="Total Paid" value={`৳ ${currentPaid}`} isBold />
                  <div className="flex justify-between py-2 text-sm font-bold text-red-600 border-t mt-2">
                    <span>Due Amount</span>
                    <span>৳ {currentDue.toFixed(2)}</span>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
                  <h3 className="text-gray-700 font-bold border-b pb-2 mb-3 text-sm uppercase">Patient Information</h3>
                  <InfoRow label="Patient Name" value={activeBill.patientName} />
                  <InfoRow label="Age" value={activeBill.age ? `${activeBill.age} Y` : "N/A"} />
                  <InfoRow label="Mobile" value={activeBill.userPhone} />
                  <InfoRow label="Ref Doctor" value={activeBill.referDoctorName} />
                </div>
              </div>

              {/* RIGHT: FORMS & HISTORY */}
              <div className="lg:col-span-8 space-y-6">
                {/* 1. PATIENT PAYMENT (Green) */}
                <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-[#73CB64]">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2 border-b pb-2">
                    <FaMoneyBillWave className="text-[#73CB64]" /> Receive Patient Payment
                  </h3>
                  <form onSubmit={handlePatientSubmit}>
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div>
                        <label className="text-xs font-bold block mb-1">Date</label>
                        <input type="date" className="w-full border p-2 rounded text-sm" value={patientForm.date} onChange={(e) => setPatientForm({ ...patientForm, date: e.target.value })} />
                      </div>
                      <div>
                        <label className="text-xs font-bold block mb-1">
                          Amount (৳) <span className="text-[10px] text-gray-400 font-normal">· Due: ৳{Number(currentDue).toFixed(0)}</span>
                        </label>
                        <input
                          type="number"
                          placeholder="0.00"
                          className="w-full border p-2 rounded text-sm focus:border-[#73CB64] outline-none"
                          value={patientForm.amount}
                          onChange={(e) => {
                            const val = e.target.value;
                            setPatientForm((prev) => ({
                              ...prev,
                              amount: val,
                              discount:
                                prev.discountType === "percentage"
                                  ? prev.discount
                                  : computeAutoDiscount(val, prev.discountSource),
                            }));
                          }}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold block mb-1">
                          Discount
                          {patientForm.discountSource === "agent" && (
                            <span className="text-[10px] text-amber-600 font-normal"> · Max ৳{agentCommission}</span>
                          )}
                        </label>
                        <div className="flex">
                          <input
                            type="number"
                            placeholder="0.00"
                            className="w-full border p-2 rounded-l text-sm focus:border-[#73CB64] outline-none"
                            value={patientForm.discount}
                            onChange={(e) => setPatientForm({ ...patientForm, discount: e.target.value })}
                          />
                          <select
                            className="border border-l-0 p-2 rounded-r text-sm bg-gray-50"
                            value={patientForm.discountType}
                            onChange={(e) => {
                              const t = e.target.value;
                              setPatientForm((prev) => ({
                                ...prev,
                                discountType: t,
                                discount:
                                  t === "taka"
                                    ? computeAutoDiscount(prev.amount, prev.discountSource)
                                    : "",
                              }));
                            }}
                          >
                            <option value="taka">৳</option>
                            <option value="percentage">%</option>
                          </select>
                        </div>
                        {patientForm.discountType === "percentage" && parseFloat(patientForm.discount) > 0 && (
                          <span className="text-[10px] text-gray-500 mt-0.5 block">
                            = ৳{effectiveDiscountAmount()} off due
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-3 mb-3">
                      <div className="flex-1">
                        <label className="text-xs font-bold block mb-1">Mode</label>
                        <DynamicSelect
                          options={modeOptions}
                          value={modeOptions.find((o) => o.value === patientForm.paymentMode) || null}
                          onChange={(val) => setPatientForm({ ...patientForm, paymentMode: val?.value || "Cash" })}
                          isSearchable={false}
                          placeholder="Mode"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="text-xs font-bold block mb-1">Discount By</label>
                        <DynamicSelect
                          options={discountSourceOptions}
                          value={discountSourceOptions.find((o) => o.value === patientForm.discountSource) || null}
                          onChange={(val) => {
                            const src = val?.value || "agent";
                            setPatientForm((prev) => ({
                              ...prev,
                              discountSource: src,
                              discount:
                                prev.discountType === "percentage"
                                  ? prev.discount
                                  : computeAutoDiscount(prev.amount, src),
                            }));
                          }}
                          isSearchable={false}
                          placeholder="Discount By"
                        />
                      </div>
                      <div className="flex-[2]">
                        <label className="text-xs font-bold block mb-1">
                          {patientForm.discountSource === "hospital" && parseFloat(patientForm.discount) > 0
                            ? <>Reason <span className="text-red-600">*</span></>
                            : "Note"}
                        </label>
                        <input
                          type="text"
                          className={`w-full border p-2 rounded text-sm ${
                            patientForm.discountSource === "hospital" && parseFloat(patientForm.discount) > 0
                              ? "border-red-400"
                              : ""
                          }`}
                          placeholder={
                            patientForm.discountSource === "hospital" && parseFloat(patientForm.discount) > 0
                              ? "Reason for hospital discount (required)"
                              : "Note"
                          }
                          value={patientForm.note}
                          onChange={(e) => setPatientForm({ ...patientForm, note: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button type="submit" disabled={isSubmitting} className="bg-[#73CB64] text-white px-5 py-2 rounded text-sm hover:bg-M-primary-color font-medium shadow-sm transition" >
                        {isSubmitting ? "..." : "Receive Payment"}
                      </button>
                    </div>
                  </form>
                </div>

                {/* 2. DOCTOR PAYMENT (Purple) */}
                <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-M-primary-color/90">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2 border-b pb-2">
                    <FaUserMd className="text-M-primary-color/90" /> Pay Doctor (Commission/Fee)
                  </h3>
                  <form onSubmit={handleDoctorSubmit}>
                    <div className="mb-3">
                      <label className="text-xs font-bold block mb-1">Select Doctor</label>
                      <DynamicSelect options={doctors} value={doctors.find((d) => d.value === doctorForm.doctorId)} onChange={(val) => setDoctorForm({ ...doctorForm, doctorId: val?.value })} placeholder="Search Doctor..." isClearable />
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="text-xs font-bold block mb-1">Pay Amount (৳)</label>
                        <input type="number" placeholder="0.00" className="w-full border p-2 rounded text-sm focus:border-M-primary-color/90 outline-none" value={doctorForm.amount} onChange={(e) => setDoctorForm({ ...doctorForm, amount: e.target.value })} />
                      </div>
                      <div>
                        <label className="text-xs font-bold block mb-1">Reason / Note</label>
                        <input type="text" placeholder="e.g. Commission" className="w-full border p-2 rounded text-sm" value={doctorForm.note} onChange={(e) => setDoctorForm({ ...doctorForm, note: e.target.value })} />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button type="submit" disabled={isSubmitting || !doctorForm.doctorId} className="bg-M-primary-color/90 text-white px-5 py-2 rounded text-sm hover:bg-M-primary-color font-medium shadow-sm transition disabled:opacity-50" >
                        {isSubmitting ? "..." : "Pay Doctor"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* 3. TRANSACTION HISTORY */}
            <div className="mt-8 bg-white p-5 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-gray-800 font-bold border-b pb-3 mb-4 text-sm uppercase">Transaction History</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left border border-gray-200">
                  <thead className="bg-gray-100 text-gray-600 uppercase text-xs">
                    <tr>
                      <th className="px-4 py-3 font-semibold border-b">ID</th>
                      <th className="px-4 py-3 font-semibold border-b">Date</th>
                      <th className="px-4 py-3 font-semibold border-b">Details</th>
                      <th className="px-4 py-3 font-semibold border-b text-right">Amount (৳)</th>
                      <th className="px-4 py-3 font-semibold border-b text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 text-gray-700">
                    {transactions.length > 0 ? (
                      transactions.map((tx, i) => {
                        const isDoc = tx.doctorAmount > 0;
                        let docName = "Doctor";
                        if (tx.doctor?.translations) {
                          try {
                            const t = typeof tx.doctor.translations === "string" ? JSON.parse(tx.doctor.translations) : tx.doctor.translations;
                            docName = t?.en?.name || docName;
                          } catch (e) {}
                        } else if (tx.doctor?.name) {
                          docName = tx.doctor.name;
                        }

                        return (
                          <tr key={i} className={`hover:bg-gray-50 ${isDoc ? "bg-purple-50/40" : ""}`} >
                            <td className="px-4 py-3 font-mono text-xs text-gray-500">{tx.id ? `PAY-${tx.id}` : "-"}</td>
                            <td className="px-4 py-3 whitespace-nowrap">{new Date(tx.paymentDate).toLocaleDateString("en-GB")}</td>
                            <td className="px-4 py-3">
                              {isDoc ? (
                                <div className="flex flex-col">
                                  <span className="text-xs font-bold text-M-primary-color/90 flex items-center gap-1"><FaUserMd size={10} /> Paid to: {docName}</span>
                                  {tx.doctorNote && <span className="text-[10px] text-gray-500 italic mt-0.5">{tx.doctorNote}</span>}
                                </div>
                              ) : (
                                <div className="flex flex-col">
                                  <span className="text-xs font-bold text-[#73CB64] flex items-center gap-1"><FaMoneyBillWave size={10} /> Received from Patient</span>
                                  <span className="text-[10px] text-gray-400 mt-0.5">Via {tx.paymentMode} {tx.note ? `- ${tx.note}` : ""}</span>
                                  {tx.userName && <span className="text-[9px] text-blue-500 font-semibold mt-0.5">Received By: {tx.userName}</span>}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-sm">
                              {isDoc ? <span className="text-red-500">- {parseFloat(tx.doctorAmount).toFixed(2)}</span> : <span className="text-[#73CB64]">+ {parseFloat(tx.amount).toFixed(2)}</span>}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex justify-center items-center gap-3">
                                <button onClick={() => handlePrintReceipt(tx)} className="text-blue-500 hover:text-blue-700 transition" title="Print"><FaPrint /></button>
                                <button onClick={() => initiateDelete(tx.id)} className="text-red-500 hover:text-red-700 transition" title="Delete"><FaTrash /></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-400 italic">No transactions found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden Print Area */}
      {printData && (
            <PathologyPrintSlip className="payment-history-print" title={printData.title} billInfo={printData.billInfo} table={printData.table} summary={printData.summary} footer={printData.footer} note={printData.note} isPaid={printData.isPaid} isFree={printData.isFree} amountInWords={printData.amountInWords} />
      )}

      {/* Delete Confirmation */}
      <ConfirmActionModal isOpen={confirmDelete.show} onClose={() => setConfirmDelete({ show: false, paymentId: null })} onConfirm={executeDelete} ids={[confirmDelete.paymentId]} entityName="payment" title="Delete Payment" message="Are you sure? This will revert the bill balance." confirmText="Delete" cancelText="Cancel" />
    </>
  );
};

export default PathologyAddPaymentModal;